import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useId, useState } from 'react';
import { Bell } from 'lucide-react';
import { cn } from 'lib/utils';
import CompanyStore from 'stores/CompanyStore/store';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { feedItemBodyHtml, isProducerCheckinItem, isCheckinInteractive, producerOpenUrl, isCheckinDismissable } from 'utils/KnockFeedUtils';
import { feedBodyHasLink, feedBodyPlainText } from 'utils/sanitizeFeedHtml';
import ProducerBellRowAction, { keepFocusOnHover } from './ProducerBellRowAction';
var timeLabel = function (iso) { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); };
/**
 * After an acknowledge the selected control unmounts, so focus is placed
 * explicitly rather than left to Radix's recovery (which lands on the first
 * item): the next row control, else the previous one, else the first
 * read-only history row, else the menu itself (with no other row, Radix's
 * keyboard entry focus then moves into the acknowledged row once it re-renders
 * as a history item). Runs before the state update, while the target (another
 * row) is still mounted.
 */
var focusAfterAcknowledge = function (selected) {
    var _a, _b, _c;
    var menu = selected.closest('[role="menu"]');
    if (!menu) {
        return;
    }
    var controls = Array.from(menu.querySelectorAll('[data-row-action]'));
    var index = controls.indexOf(selected);
    var historyRow = menu.querySelector('[role="menuitem"]:not([data-row-action])');
    var target = (_c = (_b = (index >= 0 ? (_a = controls[index + 1]) !== null && _a !== void 0 ? _a : controls[index - 1] : undefined)) !== null && _b !== void 0 ? _b : historyRow) !== null && _c !== void 0 ? _c : menu;
    target.focus();
};
/**
 * The producer notifications bell (launch requirement, locked 2026-07-02):
 * Virtual Coach messages log here with an 8-hour rolloff — same-day "what was said
 * and when" for anyone who missed the banner, gone by tomorrow. Acknowledged
 * items stay listed (muted) for the day; un-acknowledged items acknowledge on
 * tap. No permanent ledger at launch — retention is a product dial.
 *
 * Renders nothing while the window is empty, mirroring the coach feed's
 * dark-safe posture.
 */
var ProducerKnockBell = function (_a) {
    var items = _a.items, onViewed = _a.onViewed, onAcknowledge = _a.onAcknowledge, onRespond = _a.onRespond, onOpenAction = _a.onOpenAction, now = _a.now, page = _a.page;
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var rowIdPrefix = useId();
    useEffect(function () {
        if (open && items.length > 0) {
            onViewed(items);
        }
    }, [open, items, onViewed]);
    if (items.length === 0) {
        return null;
    }
    var unacknowledged = items.filter(function (item) { return item.interacted_at === null; });
    var isCoach = page === 'coach';
    // Acknowledging keeps the list open, as a mouse click on the old native
    // button did, so a producer can clear several rows in one visit. Answers
    // (Yes/No) and Open close it.
    var acknowledge = function (item) { return function (event) {
        event.preventDefault();
        if (event.currentTarget instanceof HTMLElement) {
            focusAfterAcknowledge(event.currentTarget);
        }
        onAcknowledge(item);
    }; };
    /** The row's control, if any (see RowControl). */
    var renderControl = function (item) {
        if (item.interacted_at !== null) {
            return null;
        }
        // An unanswered attendance ask never offers "Got it".
        if (isProducerCheckinItem(item)) {
            // Past the 60-min sweep the row is resolved and a tap would
            // no-op — same no-zombie-buttons rule the banner and the
            // coach feed follow (escalation ADR §3). Dismissal is safe
            // once expired, so the row falls back to "Got it".
            if (!isCheckinInteractive(item, now)) {
                // Dismissal only once skew cannot explain the expiry —
                // same asymmetric boundary the banner uses.
                return isCheckinDismissable(item, now) ? {
                    actionable: true,
                    node: (_jsx(ProducerBellRowAction, { variant: "outline", onSelect: acknowledge(item), children: "Expired \u2014 dismiss" }))
                } : {
                    actionable: false,
                    node: _jsx("span", { className: "text-xs text-muted-foreground", children: "Expired" })
                };
            }
            return {
                actionable: true,
                node: (_jsxs("span", { className: "flex gap-2", children: [_jsx(ProducerBellRowAction, { variant: "primary", onSelect: function () { return onRespond(item, 'yes'); }, children: "Yes" }), _jsx(ProducerBellRowAction, { variant: "outline", onSelect: function () { return onRespond(item, 'no'); }, children: "No" })] }))
            };
        }
        // The scoring nudge's tap navigates (SCO-504 Owner
        // contract), and so does the midday's own link to this app
        // (VC-02: "Your expectations are here"). Other
        // action_url carriers keep "Got it"; their tap still just means
        // "I heard you".
        var actionUrl = producerOpenUrl(item, {
            origin: window.location.origin,
            apiEndpoint: CompanyStore.getApiEndpoint(),
            hasLink: feedBodyHasLink
        });
        if (actionUrl !== null) {
            return {
                actionable: true,
                node: (_jsx(ProducerBellRowAction, { variant: "primary", onSelect: function () { return onOpenAction(item, actionUrl); }, children: "Open" }))
            };
        }
        return {
            actionable: true,
            node: (_jsx(ProducerBellRowAction, { variant: "primary", onSelect: acknowledge(item), children: "Got it" }))
        };
    };
    var renderItem = function (item) {
        var _a;
        var acknowledged = item.interacted_at !== null;
        var control = renderControl(item);
        var bodyId = "".concat(rowIdPrefix, "-").concat(item.id, "-body");
        var timeId = "".concat(rowIdPrefix, "-").concat(item.id, "-time");
        var content = (_jsxs(_Fragment, { children: [_jsx("div", { id: bodyId, className: "whitespace-pre-line", children: feedBodyPlainText(feedItemBodyHtml(item.blocks)) }), _jsxs("div", { className: "mt-2 flex items-center justify-between gap-2", children: [_jsx("span", { id: timeId, className: "text-xs text-muted-foreground", children: timeLabel(item.inserted_at) }), (_a = control === null || control === void 0 ? void 0 : control.node) !== null && _a !== void 0 ? _a : null] })] }));
        var rowClassName = cn('border-b border-border p-4 text-sm last:border-0', acknowledged && 'opacity-60');
        // A row with controls is a group named by its message and time, so a
        // screen reader hears what "Yes" answers and when it was said.
        if (control === null || control === void 0 ? void 0 : control.actionable) {
            return (_jsx(DropdownMenuGroup, { "aria-labelledby": "".concat(bodyId, " ").concat(timeId), className: rowClassName, children: content }, item.id));
        }
        // A row with nothing to operate (acknowledged, or plain "Expired") is
        // itself a read-only item: focusable so the day's history can be read
        // by keyboard or screen reader, announced as not operable, and the
        // menu never ends up owning no items once everything is acknowledged.
        // `aria-disabled` rather than Radix `disabled`, which arrows skip.
        return (_jsx(DropdownMenuItem, { "aria-disabled": "true", onSelect: function (event) { return event.preventDefault(); }, onPointerMove: keepFocusOnHover, onPointerLeave: keepFocusOnHover, 
            // Keyboard-only highlight; hover neither focuses nor lights the row
            // (it does nothing). Text stays selectable, unlike the base item's
            // select-none.
            className: cn('block cursor-default select-text rounded-none focus:bg-transparent focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', rowClassName), children: content }, item.id));
    };
    return (_jsxs(DropdownMenu, { onOpenChange: setOpen, children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsxs("button", { type: "button", "aria-label": unacknowledged.length ?
                        "Virtual Coach notifications \u2014 ".concat(unacknowledged.length, " unacknowledged") :
                        'Virtual Coach notifications', className: cn('relative flex h-9 w-9 items-center justify-center rounded-md border-0 bg-transparent p-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', isCoach ?
                        'text-white hover:bg-white/10' :
                        'text-primary hover:bg-black/5'), children: [_jsx(Bell, { className: "h-5 w-5" }), unacknowledged.length ? (_jsx("span", { className: "absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground", children: unacknowledged.length })) : null] }) }), _jsx(DropdownMenuContent, { align: "end", loop: true, className: "z-[1000000] w-80 p-0", children: _jsx("div", { className: "sb-knock-feed max-h-[70vh] overflow-y-auto", children: items.map(renderItem) }) })] }));
};
export default ProducerKnockBell;
